import * as React from "react";
import { createContext, useContext, ReactNode } from "react";
import PracticesService from "../Services/PracticesService";
import { IIdName } from "../Services/CommonService";
import { useUser } from "./UserContext";
import { FullPageSpinner } from "../Common/FullPageSpinner";

interface PracticeContextType {
    allPracticeIds: IPractices[];
    loading: boolean;
    addPractice: (item: IPractices) => void;
    updatePractice: (item: IPractices) => void;
}

export interface IPractices {
    value: number;
    label: string;
}

const PracticeContext = createContext<PracticeContextType | undefined>(undefined);
PracticeContext.displayName = "PracticeContext";

interface PracticeProviderProps {
    children: ReactNode;
}

const PracticeProvider: React.FC<PracticeProviderProps> = ({ children }) => {
    const { isAuthenticated } = useUser();
    const isAuth = isAuthenticated();
    const [allPracticeIds, setAllPracticeIds] = React.useState<IPractices[]>([]);
    const [loading, setLoading] = React.useState(false);

    const fetchPractices = async () => {
        setLoading(true);
        try {
            const rec = await PracticesService.getPractice();
            const items = rec?.result;

            if (items && items.length > 0) {
                const list = items
                    .map((p: IIdName) => ({
                        value: p.id,
                        label: p.name,
                    }))
                    .sort((a: IPractices, b: IPractices) =>
                        (a.label || "").localeCompare(b.label || "", undefined, {
                            sensitivity: "base",
                            numeric: true,
                        })
                    );

                setAllPracticeIds(list);
            } else {
                console.log("Can't get practices");
                setLoading(false);
            }
        } catch (err: any) {
            setLoading(false);
            console.log("Error:", err?.message || "API Error");
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (isAuth) {
            fetchPractices();
        }
    }, [isAuth]);

    const addPractice = (item: IPractices) => {
        setAllPracticeIds((prev) =>
            [...prev, item].sort((a, b) =>
                (a.label || "").localeCompare(b.label || "", undefined, {
                    sensitivity: "base",
                    numeric: true,
                })
            )
        );
    };

    const updatePractice = (updated: IPractices) => {
        setAllPracticeIds((prev) =>
            prev
                .map((p) => (p.value === updated.value ? updated : p))
                .sort((a, b) =>
                    (a.label || "").localeCompare(b.label || "", undefined, {
                        sensitivity: "base",
                        numeric: true,
                    })
                )
        );
    };

    if (loading) {
        return <FullPageSpinner label='Loading...' />;
    }
    return (
        <PracticeContext.Provider value={{ allPracticeIds, addPractice, updatePractice, loading }}>
            {children}
        </PracticeContext.Provider>
    );
};

export default PracticeProvider;

export const usePractice = () => {
    const context = useContext(PracticeContext);
    if (!context) {
        throw new Error("usePractice must be used within PracticeProvider");
    }
    return context;
};